import { store } from "@redux/store";
import { selectConfigurationReCaptchaSiteKey } from "layout/redux/selectors";

/**
 * A class to manage the integration and functionality of Google reCAPTCHA v2 (invisible mode).
 * This class provides methods to render the reCAPTCHA widget, handle token generation, and validate
 * the reCAPTCHA response.
 */
export default class ReCaptchaToken {
  /**
   * Initializes a new instance of the ReCaptchaToken class.
   * Sets up default properties such as element IDs, site key, and placeholders for the reCAPTCHA widget.
  */
  constructor() {
    this.id = 'reCaptchaToken';
    this.recaptchaPlaceHolderId = 'recaptcha-placeholder';
    this.widgetId = null;
    this.siteKey = selectConfigurationReCaptchaSiteKey(store.getState());
    this.resolveValidation = null;
    this.input = `<input type="hidden" data-qa="re-captcha-token" data-re-captcha-token id="${this.id}">`;
    this.placeHolder = `<div id="${this.recaptchaPlaceHolderId}" style="display:none;"></div>`;
    this.disclaimer = `<span hidden data-re-captcha-legal class="form__legal">This site is protected by reCAPTCHA and the Google <a class="nhs-c-standard-link--underlined" href="https://policies.google.com/privacy">Privacy Policy</a> and <a class="nhs-c-standard-link--underlined" href="https://policies.google.com/terms">Terms of Service</a> apply.</span>`;
  }

  /**
   * Generates the HTML markup for the reCAPTCHA input and placeholder elements.
   * @returns {string} The HTML string containing the input and placeholder elements.
   */
  getHtml() {
    return `${this.input}${this.placeHolder}${this.disclaimer}`;
  }

  /**
   * Retrieves the DOM element for the reCAPTCHA placeholder.
   * @returns {HTMLElement|null} The placeholder element or null if not found.
  */
  getPlaceHolder() {
    return document.getElementById(this.recaptchaPlaceHolderId);
  }

  /**
   * Retrieves the DOM element for the reCAPTCHA input field.
   * @returns {HTMLInputElement|null} The input element or null if not found.
  */
  getInput() {
    return document.getElementById(this.id);
  }

  /**
   * Sets the value of the reCAPTCHA input field.
   * @param {string} value - The token value to set in the input field.
  */
  setValue(value) {
    this.getInput().value = value;
  }

  /**
   * Gets the current value of the reCAPTCHA input field.
   * @returns {string} The token value stored in the input field.
  */
  getValue() {
    return this.getInput().value;
  }

  /**
   * Handles the successful retrieval of a reCAPTCHA token.
   * 
   * @param {string} token - The reCAPTCHA token received upon successful validation.
   * @returns {void}
   */
  onSuccess(token) {
    this.setValue(token);
    if (this.resolveValidation) {
      this.resolveValidation(true);
      this.resolveValidation = null;
    }
  }

  /**
   * Binds the reCAPTCHA widget to the placeholder element and sets up the callback for token generation.
   * This method initializes the widget using the Google reCAPTCHA API.
  */
  bindEvents() {
    this.widgetId = window.grecaptcha?.render?.(this.getPlaceHolder(), {
      sitekey: this.siteKey,
      callback: this.onSuccess.bind(this),
      size: 'invisible'
    });
  }

  /**
   * Validates the reCAPTCHA response.
   * If the reCAPTCHA token is not already generated, it triggers the reCAPTCHA execution.
   * @returns {Promise<boolean>} A promise that resolves to true if validation is successful, otherwise false.
  */
  validate() {
    const input = this.getInput();
    const isRequired = input?.hasAttribute('required');
    const isWidgetIdValid = this.widgetId !== null && this.widgetId !== undefined;
    const isGrecaptchaAvailable = typeof window.grecaptcha?.execute === 'function';
  
    if (!isRequired || !isGrecaptchaAvailable || !isWidgetIdValid || this.getValue()) {
      return Promise.resolve(true);
    }
  
    return new Promise((resolve) => {
      this.resolveValidation = resolve;
      window.grecaptcha.execute(this.widgetId);
    });
  }
}